import React, { useState, useMemo } from 'react';
import { useParams, Link } from 'react-router-dom';
import { FiStar } from 'react-icons/fi';
import ProductCard from '../../components/ProductCard/ProductCard';
import PriceDisplay from '../../components/PriceDisplay/PriceDisplay';
import Buy3Banner from '../../components/Buy3Banner/Buy3Banner';
import { CONFIG } from '../../config/config';
import { PRODUCTS } from '../../data/products.js'; // ✅ FIXED
import { useCart } from '../../contexts/CartContext';
import { hasEarlyAccess, useEarlyAccessListener } from '../../utils/earlyAccess';
import { getPricePresentation } from '../../utils/pricing';
import './ProductDetail.css';

const ProductDetail = () => {
  useEarlyAccessListener();

  const { id } = useParams();
  const { addToCart, loading: cartLoading } = useCart();

  const [selectedSize, setSelectedSize] = useState('');
  const [selectedColor, setSelectedColor] = useState('');
  const [quantity, setQuantity] = useState(1);
  const [activeImage, setActiveImage] = useState(0);

  // ✅ Product (safe)
  const product = useMemo(() => {
    return {
      id: parseInt(id, 10) || 1,
      name: CONFIG.PRODUCT.NAME,
      description: CONFIG.PRODUCT.DESCRIPTION,
      longDescription: CONFIG.PRODUCT.DETAILS_EXTRA,
      images: CONFIG.PRODUCT.IMAGES || [],
      category: 't-shirts',
      sizes: CONFIG.PRODUCT.SIZES || [],
      colors: CONFIG.PRODUCT.COLORS || [],
      features: CONFIG.PRODUCT.FEATURES || [],
      sku: CONFIG.PRODUCT.SKU,
      inStock: true,
    };
  }, [id]);

  // ✅ FIXED related products (no CONFIG dependency)
  const relatedProducts = useMemo(() => {
    return PRODUCTS.slice(0, 3);
  }, []);

  // ✅ Safety guard
  if (!product) {
    return <div>Product not found</div>;
  }

  const handleBuyNow = async () => {
    if (!selectedSize || !selectedColor) {
      alert('Please select size and color');
      return;
    }
    await addToCart(product, quantity, selectedSize, selectedColor);
  };

  const incrementQuantity = () => setQuantity((prev) => prev + 1);
  const decrementQuantity = () =>
    setQuantity((prev) => (prev > 1 ? prev - 1 : 1));

  const showBestDeal = hasEarlyAccess();
  const price = getPricePresentation();

  return (
    <div className="product-detail page-transition">
      <Buy3Banner />

      {/* Breadcrumb */}
      <div className="breadcrumb">
        <div className="container">
          <Link to="/">Home</Link>
          <span>/</span>
          <Link to="/products">Shop</Link>
          <span>/</span>
          <Link to={`/products?category=${product.category}`}>T-Shirts</Link>
          <span>/</span>
          <span className="current">{product.name}</span>
        </div>
      </div>

      <div className="container">
        <div className="product-detail-container">

          {/* Images */}
          <div className="product-images">
            <div className="main-image">
              <img
                src={product.images[activeImage]}
                alt={`${product.name}`}
              />
              {showBestDeal && (
                <span className="product-detail-badge">Best Deal</span>
              )}
            </div>

            <div className="thumbnail-images">
              {product.images?.map((img, index) => (
                <button
                  key={img}
                  type="button"
                  className={`thumbnail ${index === activeImage ? 'active' : ''}`}
                  onClick={() => setActiveImage(index)}
                >
                  <img src={img} alt="" />
                </button>
              ))}
            </div>
          </div>

          {/* Info */}
          <div className="product-info">
            <h1 className="product-title">{product.name}</h1>

            <div className="product-price-block">
              <PriceDisplay />
              <span className="pdp-off-badge">
                {price.discountPercent}% OFF
              </span>
            </div>

            {/* Trust */}
            <div className="product-trust-chips">
              <span className="trust-chip">{CONFIG.URGENCY.LIMITED_STOCK}</span>
              <span className="trust-chip trust-chip--accent">
                {CONFIG.URGENCY.SELLING_FAST}
              </span>
              <span className="trust-chip">{CONFIG.TRUST.COTTON}</span>
            </div>

            {/* Colors */}
            <div className="color-selection">
              <h3>Color: {selectedColor || 'Select'}</h3>
              <div className="color-options">
                {product.colors?.map((color) => (
                  <button
                    key={color.name}
                    className={`color-option ${selectedColor === color.name ? 'selected' : ''
                      }`}
                    onClick={() => setSelectedColor(color.name)}
                  >
                    <span
                      className="color-swatch"
                      style={{ backgroundColor: color.code }}
                    />
                  </button>
                ))}
              </div>
            </div>

            {/* Sizes */}
            <div className="size-selection">
              <h3>Size: {selectedSize || 'Select'}</h3>
              <div className="size-options">
                {product.sizes?.map((size) => (
                  <button
                    key={size}
                    className={`size-option ${selectedSize === size ? 'selected' : ''
                      }`}
                    onClick={() => setSelectedSize(size)}
                  >
                    {size}
                  </button>
                ))}
              </div>
            </div>

            {/* Actions */}
            <div className="product-actions">
              <div className="quantity-selector">
                <button onClick={decrementQuantity}>−</button>
                <span>{quantity}</span>
                <button onClick={incrementQuantity}>+</button>
              </div>

              <button
                className={`add-to-cart-btn ${cartLoading ? 'loading' : ''}`}
                onClick={handleBuyNow}
              >
                {cartLoading ? 'ADDING…' : CONFIG.CTA.BUY_NOW}
              </button>
            </div>

            {/* Features */}
            <ul className="features-list">
              {product.features?.map((f) => (
                <li key={f}>{f}</li>
              ))}
            </ul>

            {/* Reviews */}
            <div className="reviews">
              <h3 className="reviews-title">Customer Reviews</h3>

              {CONFIG.PRODUCT.REVIEWS?.map((r) => (
                <div key={r.author} className="review-card">

                  <div className="review-header">
                    <span className="review-author">{r.author}</span>
                  </div>

                  <div className="review-stars">
                    {[1, 2, 3, 4, 5].map((i) => (
                      <FiStar
                        key={i}
                        className={`star ${i <= r.stars ? 'filled' : ''}`}
                      />
                    ))}
                  </div>

                  <p className="review-text">"{r.text}"</p>
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* Related */}
        <section className="related-products">
          <h2>You may also like</h2>
          <div className="related-products-grid">
            {relatedProducts?.map((p) => (
              <ProductCard key={p.id} product={p} />
            ))}
          </div>
        </section>
      </div>
    </div>
  );
};

export default ProductDetail;